import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router'
import styled from '@emotion/styled'
import Axios from 'axios'
import { toast } from 'react-toastify'
import { createSimpleStore } from 'react-simple-reducer'
import { Button as NDButton } from '../components'
import { modulosActions, getModulosComEstatisticas } from '../modulos/ModulosStore'
import { progressoActions } from '../conteudo/ProgressoReduxStore'
import svgs from '../svgs'
import aplicacaoPadrao from '../assets/marca/aplicacao_padrao.png'

interface RegrasSimuladoParams {
  cursoId: string
}

interface CriarSimuladoResponse {
  acao: 'CRIADO'
  avaliacao?: {
    id: number
  }
}

type TipoSimulado = 'modulo-atual' | 'conteudo-acumulado'

interface EscopoSimulado {
  valido: boolean
  nome: string
  modulosIds: number[]
  modulosPreview: Array<{ id: number; nome: string }>
  modulosRestantes: number
  tipo: TipoSimulado | null
  moduloId: number | null
}

const MIN_QUESTOES = 5
const MAX_QUESTOES = 30
const MIN_TEMPO = 5
const MAX_TEMPO = 60
const DEFAULT_QUESTOES = 15
const DEFAULT_TEMPO = 45
const MAX_MODULOS_PREVIEW = 4

const escopoInvalido: EscopoSimulado = {
  valido: false,
  nome: '',
  modulosIds: [],
  modulosPreview: [],
  modulosRestantes: 0,
  tipo: null,
  moduloId: null,
}

const RegrasSimuladoStore = createSimpleStore(
  {
    escopo: escopoInvalido,
    quantidadeQuestoes: DEFAULT_QUESTOES,
    tempoMinutos: DEFAULT_TEMPO,
    creating: false,
  } as {
    escopo: EscopoSimulado
    quantidadeQuestoes: number
    tempoMinutos: number
    creating: boolean
  },
  {
    setEscopo(state, escopo: EscopoSimulado) {
      state.escopo = escopo
    },
    setQuantidadeQuestoes(state, quantidadeQuestoes: number) {
      state.quantidadeQuestoes = quantidadeQuestoes
    },
    setTempoMinutos(state, tempoMinutos: number) {
      state.tempoMinutos = tempoMinutos
    },
    setCreating(state, creating: boolean) {
      state.creating = creating
    },
  },
  {
    thunks: {
      createSimulado({
        cursoId,
        history,
      }: {
        cursoId: string
        history: ReturnType<typeof useHistory>
      }) {
        return async (dispatch, getState) => {
          const { escopo, quantidadeQuestoes, tempoMinutos, creating } = getState()
          if (creating) return

          if (!escopo.modulosIds.length) {
            toast.error('Não há módulos elegíveis para esse tipo de simulado')
            return
          }

          if (quantidadeQuestoes < MIN_QUESTOES || quantidadeQuestoes > MAX_QUESTOES) {
            toast.error(
              `A quantidade de questões deve estar entre ${MIN_QUESTOES} e ${MAX_QUESTOES}`
            )
            return
          }

          if (tempoMinutos < MIN_TEMPO || tempoMinutos > MAX_TEMPO) {
            toast.error(`O tempo deve estar entre ${MIN_TEMPO} e ${MAX_TEMPO} minutos`)
            return
          }

          if (tempoMinutos % 5 !== 0) {
            toast.error('O tempo deve ser ajustado de 5 em 5 minutos')
            return
          }

          try {
            dispatch({ type: 'setCreating', payload: true })
            const payload = {
              modulosIds: escopo.modulosIds,
              quantidadeQuestoes,
              tempoMinutos,
            }

            const response = await Axios.post('/app/avaliacoes/simulados/criar', payload).then(
              (x) => x.data
            )
            const dataResponse = response as CriarSimuladoResponse
            const avaliacaoId = dataResponse.avaliacao?.id

            if (!avaliacaoId) {
              throw new Error('Não foi possível criar o simulado')
            }

            history.push(`/curso/${cursoId}/simulado/${avaliacaoId}`)
          } catch (error: any) {
            const message = error?.response?.data?.message ?? 'Não foi possível criar o simulado'
            toast.error(message)
          } finally {
            dispatch({ type: 'setCreating', payload: false })
          }
        }
      },
    },
  }
)

export const RegrasSimulado = () => {
  return (
    <RegrasSimuladoStore.Provider>
      <InitRegrasSimulado />
      <RegrasSimuladoConteudo />
    </RegrasSimuladoStore.Provider>
  )
}

function useEscopoSimulado() {
  const { cursoId } = useParams<RegrasSimuladoParams>()
  const location = useLocation()
  const dispatch = useDispatch()
  const uiDispatch = RegrasSimuladoStore.useDispatch()
  const history = useHistory()

  useEffect(() => {
    if (!cursoId) return
    dispatch(modulosActions.loadModulos(+cursoId))
    dispatch(progressoActions.loadEstatisticasEstudo(+cursoId))
  }, [cursoId, dispatch])

  const modulos = useSelector(getModulosComEstatisticas)

  const modulosComConteudoEstudado = useMemo(
    () =>
      modulos.filter((modulo) => {
        const percentual = modulo.estatisticaEstudo?.percentualProgressoMaisAvancado ?? 0
        return percentual > 0
      }),
    [modulos]
  )

  const tipo = useMemo<TipoSimulado | null>(() => {
    const tipoQuery = new URLSearchParams(location.search).get('tipo')
    if (tipoQuery === 'modulo-atual' || tipoQuery === 'conteudo-acumulado') return tipoQuery
    return null
  }, [location.search])

  const moduloIdQuery = useMemo(
    () => +(new URLSearchParams(location.search).get('moduloId') || 0),
    [location.search]
  )

  const escopoCalculado = useMemo<EscopoSimulado>(() => {
    if (!tipo) {
      return escopoInvalido
    }

    if (tipo === 'modulo-atual') {
      const modulo = modulos.find((item) => item.id === moduloIdQuery)
      const percentual = modulo?.estatisticaEstudo?.percentualProgressoMaisAvancado ?? 0
      const valido = !!modulo && percentual > 0 && !modulo.estatisticaEstudo?.moduloCompleto

      return {
        valido,
        nome: modulo?.nome || '',
        modulosIds: modulo?.id ? [modulo.id] : [],
        modulosPreview: [],
        modulosRestantes: 0,
        tipo,
        moduloId: modulo?.id ?? null,
      }
    }

    const totalModulos = modulosComConteudoEstudado.length
    const modulosPreview = modulosComConteudoEstudado.slice(0, MAX_MODULOS_PREVIEW)

    return {
      valido: totalModulos > 0,
      nome: `Simulado com ${totalModulos} ${
        totalModulos === 1 ? 'módulo estudado' : 'módulos estudados'
      }`,
      modulosIds: modulosComConteudoEstudado.map((item) => item.id),
      modulosPreview,
      modulosRestantes: Math.max(totalModulos - MAX_MODULOS_PREVIEW, 0),
      tipo,
      moduloId: null,
    }
  }, [tipo, modulos, moduloIdQuery, modulosComConteudoEstudado])

  useEffect(() => {
    uiDispatch({ type: 'setEscopo', payload: escopoCalculado })
  }, [escopoCalculado, uiDispatch])

  useEffect(() => {
    if (!modulos.length) return
    if (escopoCalculado.valido) return
    history.replace(`/curso/${cursoId}/simulados`)
  }, [cursoId, escopoCalculado.valido, history, modulos.length])
}

function FazerSimuladoAction() {
  const { creating } = RegrasSimuladoStore.useState()
  const dispatch = RegrasSimuladoStore.useDispatch()
  const history = useHistory()
  const { cursoId } = useParams<RegrasSimuladoParams>()

  function handleCriarSimulado() {
    dispatch(RegrasSimuladoStore.thunks.createSimulado({ cursoId, history }))
  }

  return (
    <RegrasSimulado.ActionButton
      label="Fazer simulado"
      svg={svgs.Play}
      svgPosition="LEFT"
      loading={creating}
      onClick={handleCriarSimulado}
      style={{ marginTop: 18 }}
    />
  )
}

function RevisarConteudoAction() {
  const { escopo } = RegrasSimuladoStore.useState()
  const history = useHistory()
  const { cursoId } = useParams<RegrasSimuladoParams>()

  function goToRevisarConteudo() {
    if (escopo.tipo !== 'modulo-atual' || !escopo.moduloId) return
    history.push(`/curso/${cursoId}/conteudo/${escopo.moduloId}`)
  }

  return (
    <RegrasSimulado.ActionButton
      label="Revisar conteúdo"
      color="#007AFF"
      onClick={goToRevisarConteudo}
      style={{ marginBottom: 18 }}
    />
  )
}

function VoltarSimuladosAction() {
  const history = useHistory()
  const { cursoId } = useParams<RegrasSimuladoParams>()

  function goToSimulados() {
    history.push(`/curso/${cursoId}/simulados`)
  }

  return (
    <RegrasSimulado.ActionButton
      label="Voltar aos simulados"
      fill="OUTLINE"
      color="#007AFF"
      onClick={goToSimulados}
      style={{ marginBottom: 18 }}
    />
  )
}

function ModulosPreviewTags() {
  const {
    escopo: { modulosPreview, modulosRestantes },
  } = RegrasSimuladoStore.useState()

  const restantesLabel = modulosRestantes === 1 ? 'outro' : 'outros'

  return (
    <>
      <RegrasSimulado.EscopoDescricao>Inclui questões dos módulos:</RegrasSimulado.EscopoDescricao>
      <RegrasSimulado.ModulosSelecionados>
        {modulosPreview.map((modulo) => (
          <RegrasSimulado.ModuloTag key={modulo.id}>{modulo.nome}</RegrasSimulado.ModuloTag>
        ))}
        {modulosRestantes > 0 ? (
          <RegrasSimulado.ModuloTag>
            +{modulosRestantes} {restantesLabel}
          </RegrasSimulado.ModuloTag>
        ) : null}
      </RegrasSimulado.ModulosSelecionados>
    </>
  )
}

function ConfiguracoesSimulado() {
  const { tempoMinutos, quantidadeQuestoes } = RegrasSimuladoStore.useState()
  const dispatch = RegrasSimuladoStore.useDispatch()

  return (
    <ConfiguracoesSimuladoLayout>
      <ConfiguracoesSimuladoTitulo>Configurações do Simulado</ConfiguracoesSimuladoTitulo>
      <ConfiguracoesSimuladoGrid>
        <ConfiguracoesSimuladoItem>
          <ConfiguracoesSimuladoLabel>Tempo (minutos)</ConfiguracoesSimuladoLabel>
          <StepperVertical
            value={tempoMinutos}
            min={MIN_TEMPO}
            max={MAX_TEMPO}
            step={5}
            onChange={(value) => dispatch({ type: 'setTempoMinutos', payload: value })}
            decrementAriaLabel="Diminuir tempo"
            incrementAriaLabel="Aumentar tempo"
          />
        </ConfiguracoesSimuladoItem>
        <ConfiguracoesSimuladoItem>
          <ConfiguracoesSimuladoLabel>Quantidade de questões</ConfiguracoesSimuladoLabel>
          <StepperVertical
            value={quantidadeQuestoes}
            min={MIN_QUESTOES}
            max={MAX_QUESTOES}
            step={1}
            onChange={(value) => dispatch({ type: 'setQuantidadeQuestoes', payload: value })}
            decrementAriaLabel="Diminuir quantidade de questões"
            incrementAriaLabel="Aumentar quantidade de questões"
          />
        </ConfiguracoesSimuladoItem>
      </ConfiguracoesSimuladoGrid>
    </ConfiguracoesSimuladoLayout>
  )
}

function AcoesSimulado() {
  const {
    escopo: { tipo },
  } = RegrasSimuladoStore.useState()

  return (
    <>
      <RegrasSimulado.Subtitulo>
        Quando estiver pronto, é só clicar no botão abaixo:
      </RegrasSimulado.Subtitulo>
      <FazerSimuladoAction />
      {tipo === 'modulo-atual' ? (
        <>
          <RegrasSimulado.ContainerOu>ou</RegrasSimulado.ContainerOu>
          <RevisarConteudoAction />
        </>
      ) : null}
      <br />
      <VoltarSimuladosAction />
      <br />
      <br />
    </>
  )
}

function InitRegrasSimulado() {
  useEscopoSimulado()
  return null
}

function RegrasSimuladoConteudo() {
  const { Layout, LogoNewDriver, Modulo, Titulo } = RegrasSimulado
  const { escopo } = RegrasSimuladoStore.useState()

  return (
    <Layout>
      <LogoNewDriver src={aplicacaoPadrao} />
      <Modulo>{escopo.nome}</Modulo>
      {escopo.tipo === 'conteudo-acumulado' ? <ModulosPreviewTags /> : null}
      <Titulo>
        O que você precisa saber antes de começar o <strong>Simulado:</strong>
      </Titulo>
      <Regras />
      <ConfiguracoesSimulado />
      <AcoesSimulado />
    </Layout>
  )
}

RegrasSimulado.Layout = styled.div`
  height: 100%;
  text-align: center;
  color: #2d2d2d;
  overflow: auto;
`

RegrasSimulado.LogoNewDriver = styled.img`
  width: 134px;
  margin-top: 18px;
`

RegrasSimulado.Modulo = styled.div`
  font-weight: bold;
  font-size: 24px;
  color: #f06f30;
  margin-top: 90px;
  @media (max-width: 580px) {
    font-size: 18px;
    margin-top: 8px;
  }
`

RegrasSimulado.EscopoDescricao = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: #2d2d2d;
`

RegrasSimulado.ModulosSelecionados = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 18px;
`

RegrasSimulado.ModuloTag = styled.span`
  background: #fff2ea;
  border: 1px solid #f06f30;
  border-radius: 999px;
  font-size: 13px;
  color: #f06f30;
  font-weight: 500;
  padding: 6px 12px;
`

RegrasSimulado.Titulo = styled.div`
  margin-top: 18px;
  font-size: 24px;
  @media (max-width: 580px) {
    font-size: 18px;
  }
`

RegrasSimulado.Subtitulo = styled.div`
  font-size: 18px;
  margin-top: 38px;
  @media (max-width: 580px) {
    font-size: 14px;
    margin-top: 28px;
  }
`

RegrasSimulado.ContainerOu = styled.div`
  font-size: 18px;
  margin: 16px 0;
  @media (max-width: 580px) {
    font-size: 14px;
    margin-top: 28px;
  }
`

RegrasSimulado.ActionButton = styled(NDButton)`
  width: 231px;
  height: 48px;
  margin: 0 auto;
  .label {
    font-size: 16px;
  }
`

function Regras() {
  const { Layout } = Regras
  return (
    <Layout>
      <ol>
        <li>
          Todas as questões possuem apenas <strong>uma resposta correta</strong>, leia atentamente o
          enunciado e escolha a opção que julga ser a certa.
        </li>
        <li>
          Verifique seu <strong>sinal de internet</strong>, pois caso falte conexão enquanto
          responde o simulado, <strong>não salvaremos</strong> as respostas.
        </li>
        <li>
          O simulado é composto pelas mesmas <strong>questões da avaliação</strong>.
        </li>
      </ol>
    </Layout>
  )
}

Regras.Layout = styled.div`
  text-align: left;
  padding: 38px;
  background: #f5f5f5;
  border: 1px solid #f06f30;
  max-width: 990px;
  margin: 0 auto;
  margin-top: 28px;
  font-size: 18px;
  line-height: 34px;
  ol {
    margin: 0;
    padding-left: 22px;
  }
  li::marker {
    color: #f06f30;
    font-weight: bold;
    margin-right: 4px;
  }
  @media (max-width: 1040px) {
    margin: 0 24px;
    margin-top: 28px;
  }
  @media (max-width: 580px) {
    padding: 8px;
    font-size: 14px;
    line-height: 26px;
    margin-top: 12px;
  }
`

const ConfiguracoesSimuladoLayout = styled.div`
  text-align: left;
  padding: 30px 38px 30px;
  max-width: 990px;
  background: #f5f5f5;
  border: 1px solid #f06f30;
  margin: 0 auto;
  margin-top: 16px;
  @media (max-width: 1040px) {
    margin: 16px 24px 0;
  }
  @media (max-width: 580px) {
    padding: 14px 12px 14px;
    margin-top: 12px;
  }
`

const ConfiguracoesSimuladoTitulo = styled.div`
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-align: center;
  color: #2d2d2d;
  @media (max-width: 580px) {
    display: none;
  }
`

const ConfiguracoesSimuladoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
  @media (min-width: 581px) {
    margin-top: 30px;
  }
  @media (max-width: 580px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
`

const ConfiguracoesSimuladoItem = styled.div`
  display: grid;
  justify-items: center;
  gap: 10px;
  @media (max-width: 580px) {
    gap: 8px;
  }
`

const ConfiguracoesSimuladoLabel = styled.div`
  font-size: 16px;
  color: #2d2d2d;
  text-align: center;
  @media (max-width: 580px) {
    font-size: 12px;
  }
`

function StepperVertical({
  value,
  min,
  max,
  step,
  onChange,
  decrementAriaLabel,
  incrementAriaLabel,
}) {
  const { Layout, StepButton, Value } = StepperVertical

  function handleDecrement() {
    onChange(Math.max(min, value - step))
  }

  function handleIncrement() {
    onChange(Math.min(max, value + step))
  }

  return (
    <Layout role="group">
      <StepButton
        type="button"
        onClick={handleIncrement}
        disabled={value >= max}
        aria-label={incrementAriaLabel}
      >
        <span className="chevron up" />
      </StepButton>
      <Value aria-live="polite">{value}</Value>
      <StepButton
        type="button"
        onClick={handleDecrement}
        disabled={value <= min}
        aria-label={decrementAriaLabel}
      >
        <span className="chevron down" />
      </StepButton>
    </Layout>
  )
}

StepperVertical.Layout = styled.div`
  display: grid;
  justify-items: center;
  gap: 6px;
`

StepperVertical.StepButton = styled.button`
  width: 48px;
  height: 38px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  .chevron {
    width: 10px;
    height: 10px;
    border-right: 2px solid #6f6f6f;
    border-bottom: 2px solid #6f6f6f;
    display: inline-block;
  }

  .up {
    transform: rotate(-135deg);
    margin-top: 3px;
  }

  .down {
    transform: rotate(45deg);
    margin-bottom: 3px;
  }

  &:not(:disabled):hover {
    border-color: #f06f30;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`

StepperVertical.Value = styled.strong`
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #2d2d2d;
  min-width: 64px;
  line-height: 1;
  @media (max-width: 580px) {
    font-size: 18px;
  }
`
